import React from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { Activity, Trip, getActivities, getTrips } from '../../../services/trips/api/tripsApi';
import { formatBudget } from '../../../services/trips/utils/tripUtils';
import './CalendarPage.css';

type CalendarView = 'day' | 'week' | 'month';

interface CalendarEvent {
  id: string;
  title: string;
  meta: string;
  date: string;
  startHour: number;
  durationHours: number;
  category: 'travel' | 'activity' | 'food' | 'lodging';
  tripId?: string;
  location: string;
  cost?: string;
  notes?: string;
}

interface CalendarTrip {
  id: string;
  name: string;
  color: 'forest' | 'blue';
  startDate?: string;
  endDate?: string;
  destinations?: string[];
}

interface ActivityDraft {
  title: string;
  startTime: string;
  endTime: string;
  location: string;
  cost: string;
  notes: string;
  category: CalendarEvent['category'];
  tripId?: string;
}

const TRIP_COLORS: Array<CalendarTrip['color']> = ['forest', 'blue'];
const DEFAULT_ACTIVITY_DURATION_HOURS = 1;
const hours = Array.from({ length: 11 }, (_, index) => index + 7);

const CalendarPage: React.FC = () => {
  const initialFocusDate = React.useMemo(() => startOfDay(new Date()), []);
  const [view, setView] = React.useState<CalendarView>('week');
  const [focusDate, setFocusDate] = React.useState(initialFocusDate);
  const [selectedDate, setSelectedDate] = React.useState(initialFocusDate);
  const [sidebarMonth, setSidebarMonth] = React.useState(() => startOfMonth(initialFocusDate));
  const [sidebarSelectedDate, setSidebarSelectedDate] = React.useState(initialFocusDate);
  const [calendarTrips, setCalendarTrips] = React.useState<CalendarTrip[]>([]);
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEvent | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [isAddActivityOpen, setIsAddActivityOpen] = React.useState(false);
  const [activityDraft, setActivityDraft] = React.useState<ActivityDraft>(() => createEmptyActivityDraft());

  const tripDateKeys = React.useMemo(() => buildTripDateKeys(calendarTrips), [calendarTrips]);

  React.useEffect(() => {
    let cancelled = false;

    const loadCalendarData = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const fetchedTrips = await getTrips();
        const nextCalendarTrips = fetchedTrips.map((trip, index) => mapTripToCalendarTrip(trip, index));
        const activityResults = await Promise.all(
          fetchedTrips.map(async (trip) => {
            const tripId = String(trip.id);
            try {
              const activities = await getActivities(tripId);
              return { tripId, activities };
            } catch {
              return { tripId, activities: [] as Activity[] };
            }
          }),
        );

        const nextEvents = activityResults.flatMap(({ tripId, activities }) => (
          activities
            .map((activity) => mapActivityToCalendarEvent(activity, tripId))
            .filter((event): event is CalendarEvent => event !== null)
        ));

        if (!cancelled) {
          setCalendarTrips(nextCalendarTrips);
          setEvents(nextEvents);
        }
      } catch (error) {
        if (!cancelled) {
          setCalendarTrips([]);
          setEvents([]);
          setLoadError(error instanceof Error ? error.message : 'Failed to load calendar');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCalendarData();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedDateKey = toDateKey(selectedDate);
  const focusDateKey = toDateKey(focusDate);
  const weekDays = React.useMemo(() => getWeekDays(focusDate), [focusDateKey]);
  const weekStart = weekDays[0]?.date;
  const weekEnd = weekDays[weekDays.length - 1]?.date;
  const selectedDayEvents = events.filter((event) => event.date === selectedDateKey);
  const rangeTitle = React.useMemo(() => {
    if (view === 'day') {
      return formatDayTitle(selectedDate);
    }
    if (view === 'month') {
      return formatMonthYear(focusDate);
    }
    if (weekStart && weekEnd) {
      return formatWeekRange(weekStart, weekEnd);
    }
    return formatMonthYear(focusDate);
  }, [focusDate, selectedDate, view, weekEnd, weekStart]);

  const navigationLabel = view === 'day' ? 'day' : view === 'month' ? 'month' : 'week';

  const handleMainSelectDate = (date: Date) => {
    const normalized = startOfDay(date);
    setSelectedDate(normalized);
    setFocusDate(normalized);
  };

  const handleSidebarSelectDate = (date: Date) => {
    setSidebarSelectedDate(startOfDay(date));
  };

  const goToToday = () => {
    handleMainSelectDate(new Date());
  };

  const goToPrevious = () => {
    if (view === 'day') {
      handleMainSelectDate(addDays(focusDate, -1));
      return;
    }
    if (view === 'month') {
      setFocusDate(addMonths(focusDate, -1));
      return;
    }
    setFocusDate(addDays(focusDate, -7));
  };

  const goToNext = () => {
    if (view === 'day') {
      handleMainSelectDate(addDays(focusDate, 1));
      return;
    }
    if (view === 'month') {
      setFocusDate(addMonths(focusDate, 1));
      return;
    }
    setFocusDate(addDays(focusDate, 7));
  };

  const handleViewChange = (nextView: CalendarView) => {
    setView(nextView);
    if (nextView === 'day') {
      setFocusDate(selectedDate);
    }
  };

  const openAddActivityModal = () => {
    setActivityDraft(createEmptyActivityDraft());
    setIsAddActivityOpen(true);
  };

  const closeAddActivityModal = () => {
    setIsAddActivityOpen(false);
    setActivityDraft(createEmptyActivityDraft());
  };

  const handleActivityDraftChange = (field: keyof ActivityDraft, value: string) => {
    setActivityDraft((current) => ({ ...current, [field]: value }));
  };

  const handleAddActivity = (event: React.FormEvent) => {
    event.preventDefault();

    const title = activityDraft.title.trim();
    const startHour = parseTimeToHour(activityDraft.startTime);
    const endHour = parseTimeToHour(activityDraft.endTime);

    if (!title || startHour === null || endHour === null || endHour <= startHour) {
      return;
    }

    const newEvent: CalendarEvent = {
      id: `activity-${Date.now()}`,
      title,
      meta: activityDraft.location.trim() || 'Activity',
      date: selectedDateKey,
      startHour,
      durationHours: Number((endHour - startHour).toFixed(2)),
      category: activityDraft.category,
      tripId: activityDraft.tripId || undefined,
      location: activityDraft.location.trim() || 'TBD',
      cost: activityDraft.cost.trim() || undefined,
      notes: activityDraft.notes.trim() || undefined,
    };

    setEvents((current) => [...current, newEvent]);
    setSelectedEvent(newEvent);
    closeAddActivityModal();
  };

  const handleUpdateEvent = (eventId: string, updates: Partial<CalendarEvent>) => {
    setEvents((current) => current.map((item) => (
      item.id === eventId ? { ...item, ...updates } : item
    )));
    setSelectedEvent((current) => (
      current?.id === eventId ? { ...current, ...updates } : current
    ));
  };

  const handleRemoveEvent = (eventId: string) => {
    setEvents((current) => current.filter((item) => item.id !== eventId));
    setSelectedEvent((current) => (current?.id === eventId ? null : current));
  };

  React.useEffect(() => {
    if (!isAddActivityOpen) return undefined;

    const handleEscape = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') {
        closeAddActivityModal();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAddActivityOpen]);

  return (
    <DashboardLayout activeSection="calendar" wide>
      <div className="v2-calendar-page">
        <aside className="v2-calendar-page__sidebar">
          <button type="button" className="v2-calendar-page__add" onClick={openAddActivityModal}>
            + Add to calendar
          </button>

          <MiniCalendar
            visibleMonth={sidebarMonth}
            selectedDate={sidebarSelectedDate}
            tripDateKeys={tripDateKeys}
            onSelectDate={handleSidebarSelectDate}
            onPreviousMonth={() => setSidebarMonth((month) => addMonths(month, -1))}
            onNextMonth={() => setSidebarMonth((month) => addMonths(month, 1))}
          />

          <TripsLegendToggle trips={calendarTrips} />

          <EventDetails
            className="v2-calendar-page__sidebar-details"
            event={selectedEvent}
            trips={calendarTrips}
            onClose={() => setSelectedEvent(null)}
            onUpdate={handleUpdateEvent}
            onRemove={handleRemoveEvent}
          />
        </aside>

        <main className="v2-calendar-page__main">
          {isLoading && (
            <p className="v2-calendar-page__status">Loading trips and activities...</p>
          )}
          {!isLoading && loadError && (
            <p className="v2-calendar-page__status v2-calendar-page__status--error">{loadError}</p>
          )}
          <header className="v2-calendar-page__header">
            <button type="button" className="v2-calendar-page__today" onClick={goToToday}>Today</button>
            <div className="v2-calendar-page__range">
              <button type="button" aria-label={`Previous ${navigationLabel}`} onClick={goToPrevious}>‹</button>
              <h1>{rangeTitle}</h1>
              <button type="button" aria-label={`Next ${navigationLabel}`} onClick={goToNext}>›</button>
            </div>
            <div className="v2-calendar-page__view-toggle">
              {(['day', 'week', 'month'] as CalendarView[]).map((viewOption) => (
                <button
                  type="button"
                  key={viewOption}
                  className={view === viewOption ? 'v2-calendar-page__view-toggle--active' : undefined}
                  onClick={() => handleViewChange(viewOption)}
                >
                  {capitalize(viewOption)}
                </button>
              ))}
            </div>
          </header>

          {view === 'week' && (
            <WeekCalendar
              events={events}
              weekDays={weekDays}
              trips={calendarTrips}
              selectedDateKey={selectedDateKey}
              onSelectDate={handleMainSelectDate}
              onSelectEvent={setSelectedEvent}
            />
          )}

          {view === 'day' && (
            <DayCalendar
              selectedDate={selectedDate}
              events={selectedDayEvents}
              onSelectEvent={setSelectedEvent}
            />
          )}

          {view === 'month' && (
            <MonthCalendar
              events={events}
              focusDate={focusDate}
              selectedDateKey={selectedDateKey}
              onSelectDate={handleMainSelectDate}
              onSelectEvent={setSelectedEvent}
            />
          )}
        </main>
      </div>

      {isAddActivityOpen && (
        <AddActivityModal
          selectedDate={selectedDate}
          draft={activityDraft}
          trips={calendarTrips}
          onDraftChange={handleActivityDraftChange}
          onSubmit={handleAddActivity}
          onCancel={closeAddActivityModal}
        />
      )}
    </DashboardLayout>
  );
};

interface CalendarDay {
  label: string;
  date: Date;
  dateKey: string;
}

interface MiniCalendarProps {
  visibleMonth: Date;
  selectedDate: Date;
  tripDateKeys: Set<string>;
  onSelectDate: (date: Date) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({
  visibleMonth,
  selectedDate,
  tripDateKeys,
  onSelectDate,
  onPreviousMonth,
  onNextMonth,
}) => {
  const monthCells = React.useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const selectedDateKey = toDateKey(selectedDate);

  return (
    <section className="v2-mini-calendar" aria-label={`${formatMonthYear(visibleMonth)} mini calendar`}>
      <header>
        <h2>{formatMonthYear(visibleMonth)}</h2>
        <div>
          <button type="button" aria-label="Previous month" onClick={onPreviousMonth}>‹</button>
          <button type="button" aria-label="Next month" onClick={onNextMonth}>›</button>
        </div>
      </header>
      <div className="v2-mini-calendar__weekdays">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>
      <div className="v2-mini-calendar__days">
        {monthCells.map((cell) => {
          const isTripDay = tripDateKeys.has(cell.dateKey);
          const isSelected = cell.dateKey === selectedDateKey;

          if (!cell.inMonth) {
            return (
              <span key={cell.dateKey} className="v2-mini-calendar__day--muted">
                {cell.date.getDate()}
              </span>
            );
          }

          return (
            <button
              type="button"
              key={cell.dateKey}
              onClick={() => onSelectDate(cell.date)}
              className={[
                isTripDay ? 'v2-mini-calendar__day--trip' : '',
                isSelected ? 'v2-mini-calendar__day--selected' : '',
              ].filter(Boolean).join(' ')}
            >
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
    </section>
  );
};

interface WeekCalendarProps {
  events: CalendarEvent[];
  weekDays: CalendarDay[];
  trips: CalendarTrip[];
  selectedDateKey: string;
  onSelectDate: (date: Date) => void;
  onSelectEvent: (event: CalendarEvent) => void;
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({
  events,
  weekDays,
  trips,
  selectedDateKey,
  onSelectDate,
  onSelectEvent,
}) => {
  const tripsInWeek = React.useMemo(
    () => getTripsInWeek(trips, weekDays),
    [trips, weekDays],
  );

  return (
  <section className="v2-calendar-week" aria-label="Week calendar">
    <div className="v2-calendar-week__days">
      <div />
      {weekDays.map((day) => (
        <button
          type="button"
          key={day.dateKey}
          onClick={() => onSelectDate(day.date)}
          className={day.dateKey === selectedDateKey ? 'v2-calendar-week__day--active' : undefined}
        >
          <span>{day.label}</span>
          <strong>{day.date.getDate()}</strong>
        </button>
      ))}
    </div>

    <div className="v2-calendar-week__all-day">
      <span>All-day</span>
      {tripsInWeek.map((trip) => (
        <div key={trip.id} className="v2-calendar-week__all-day-event">
          {formatTripAllDayLabel(trip)}
        </div>
      ))}
    </div>

    <div className="v2-calendar-week__grid">
      <div className="v2-calendar-week__times">
        {hours.map((hour) => (
          <span key={hour}>{formatHour(hour)}</span>
        ))}
      </div>
      <div className="v2-calendar-week__columns">
        {weekDays.map((day) => (
          <div
            key={day.dateKey}
            className={`v2-calendar-week__column ${day.dateKey === selectedDateKey ? 'v2-calendar-week__column--active' : ''}`}
          >
            {events
              .filter((event) => event.date === day.dateKey)
              .map((event) => (
                <CalendarEventBlock key={event.id} event={event} onSelect={onSelectEvent} />
              ))}
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

interface DayCalendarProps {
  selectedDate: Date;
  events: CalendarEvent[];
  onSelectEvent: (event: CalendarEvent) => void;
}

const DayCalendar: React.FC<DayCalendarProps> = ({ selectedDate, events, onSelectEvent }) => (
  <section className="v2-calendar-day" aria-label={`Day calendar for ${formatDayTitle(selectedDate)}`}>
    <header>
      <span>{getWeekdayLabel(selectedDate)}</span>
      <strong>{selectedDate.getDate()}</strong>
      <p>{events.length} scheduled item{events.length === 1 ? '' : 's'}</p>
    </header>
    <div className="v2-calendar-day__timeline">
      {hours.map((hour) => (
        <div key={hour} className="v2-calendar-day__row">
          <time>{formatHour(hour)}</time>
          <div>
            {events
              .filter((event) => Math.floor(event.startHour) === hour)
              .map((event) => (
                <button
                  type="button"
                  key={event.id}
                  className={`v2-calendar-day__event v2-calendar-day__event--${event.category}`}
                  onClick={() => onSelectEvent(event)}
                >
                  <strong>{formatEventTime(event.startHour)} {event.title}</strong>
                  <span>{event.meta}</span>
                </button>
              ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

interface MonthCalendarProps {
  events: CalendarEvent[];
  focusDate: Date;
  selectedDateKey: string;
  onSelectDate: (date: Date) => void;
  onSelectEvent: (event: CalendarEvent) => void;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  events,
  focusDate,
  selectedDateKey,
  onSelectDate,
  onSelectEvent,
}) => {
  const monthCells = React.useMemo(() => buildMonthCells(focusDate), [focusDate]);

  return (
  <section className="v2-calendar-month" aria-label={`Month calendar for ${formatMonthYear(focusDate)}`}>
    <div className="v2-calendar-month__weekdays">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <span key={day}>{day}</span>
      ))}
    </div>
    <div className="v2-calendar-month__grid">
      {monthCells.map((cell) => {
        if (!cell.inMonth) {
          return (
            <div key={cell.dateKey} className="v2-calendar-month__cell v2-calendar-month__cell--muted">
              <span>{cell.date.getDate()}</span>
            </div>
          );
        }

        const dayEvents = events.filter((event) => event.date === cell.dateKey);

        return (
          <button
            type="button"
            key={cell.dateKey}
            className={`v2-calendar-month__cell ${cell.dateKey === selectedDateKey ? 'v2-calendar-month__cell--selected' : ''}`}
            onClick={() => onSelectDate(cell.date)}
          >
            <span>{cell.date.getDate()}</span>
            {dayEvents.slice(0, 2).map((event) => (
              <em
                key={event.id}
                className={`v2-calendar-month__event v2-calendar-month__event--${event.category}`}
                onClick={(eventClick) => {
                  eventClick.stopPropagation();
                  onSelectEvent(event);
                }}
              >
                {event.title}
              </em>
            ))}
            {dayEvents.length > 2 && <small>+{dayEvents.length - 2} more</small>}
          </button>
        );
      })}
    </div>
  </section>
  );
};

const categoryLegend: Array<{ color: LegendColor; label: string }> = [
  { color: 'green', label: 'Activities' },
  { color: 'gold', label: 'Food & dining' },
  { color: 'blue', label: 'Travel' },
  { color: 'forest', label: 'Lodging' },
];

type LegendColor = 'forest' | 'blue' | 'green' | 'gold';

const LegendItem: React.FC<{ color: LegendColor; label: string }> = ({ color, label }) => (
  <div className="v2-calendar-page__legend-item">
    <span className={`v2-calendar-page__legend-dot v2-calendar-page__legend-dot--${color}`} />
    <p>{label}</p>
  </div>
);

const TripsLegendToggle: React.FC<{ trips: CalendarTrip[] }> = ({ trips }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="v2-calendar-legend-toggle" ref={containerRef}>
      <button
        type="button"
        className={`v2-calendar-legend-toggle__button ${isOpen ? 'v2-calendar-legend-toggle__button--open' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="v2-calendar-legend-toggle__preview" aria-hidden>
          {trips.map((trip) => (
            <span key={trip.id} className={`v2-calendar-page__legend-dot v2-calendar-page__legend-dot--${trip.color}`} />
          ))}
          {categoryLegend.slice(0, 2).map((item) => (
            <span key={item.label} className={`v2-calendar-page__legend-dot v2-calendar-page__legend-dot--${item.color}`} />
          ))}
        </span>
        <span className="v2-calendar-legend-toggle__label">Trips & colors</span>
        <span className="v2-calendar-legend-toggle__chevron" aria-hidden>{isOpen ? '▴' : '▾'}</span>
      </button>

      {isOpen && (
        <div className="v2-calendar-legend-toggle__menu" role="menu" aria-label="Trips and color legend">
          <p className="v2-calendar-legend-toggle__section">My trips</p>
          {trips.length === 0 && (
            <p className="v2-calendar-legend-toggle__empty">No trips yet</p>
          )}
          {trips.map((trip) => (
            <LegendItem key={trip.id} color={trip.color} label={trip.name} />
          ))}
          <p className="v2-calendar-legend-toggle__section">Categories</p>
          {categoryLegend.map((item) => (
            <LegendItem key={item.label} color={item.color} label={item.label} />
          ))}
        </div>
      )}
    </div>
  );
};

interface CalendarEventBlockProps {
  event: CalendarEvent;
  onSelect: (event: CalendarEvent) => void;
}

const CalendarEventBlock: React.FC<CalendarEventBlockProps> = ({ event, onSelect }) => {
  const top = ((event.startHour - 7) / 11) * 100;
  const height = (event.durationHours / 11) * 100;

  return (
    <button
      type="button"
      className={`v2-calendar-event v2-calendar-event--${event.category}`}
      onClick={() => onSelect(event)}
      style={{
        top: `${top}%`,
        height: `${height}%`,
      }}
    >
      <strong>{formatEventTime(event.startHour)} {event.title}</strong>
      <span>{event.meta}</span>
    </button>
  );
};

interface EventDetailsProps {
  event: CalendarEvent | null;
  trips: CalendarTrip[];
  onClose: () => void;
  onUpdate: (eventId: string, updates: Partial<CalendarEvent>) => void;
  onRemove: (eventId: string) => void;
  className?: string;
}

type EventEditMode = 'none' | 'time' | 'place';

interface TimeEditDraft {
  date: string;
  startTime: string;
  endTime: string;
}

const EventDetails: React.FC<EventDetailsProps> = ({
  event,
  trips,
  onClose,
  onUpdate,
  onRemove,
  className,
}) => {
  const [editMode, setEditMode] = React.useState<EventEditMode>('none');
  const [timeDraft, setTimeDraft] = React.useState<TimeEditDraft>({
    date: '',
    startTime: '',
    endTime: '',
  });
  const [placeDraft, setPlaceDraft] = React.useState('');

  React.useEffect(() => {
    setEditMode('none');
    setPlaceDraft(event?.location || '');
    if (event) {
      setTimeDraft({
        date: event.date,
        startTime: formatEventTime(event.startHour),
        endTime: formatEventTime(event.startHour + event.durationHours),
      });
    }
  }, [event?.id, event?.date, event?.startHour, event?.durationHours, event?.location]);

  if (!event) {
    return (
      <section className={`v2-calendar-event-details v2-calendar-event-details--empty ${className || ''}`.trim()}>
        <h2>Event details</h2>
        <p>Select an itinerary block to see details and edit options.</p>
      </section>
    );
  }

  const trip = trips.find((item) => item.id === event.tripId);

  const handleSaveTime = (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();

    const startHour = parseTimeToHour(timeDraft.startTime);
    const endHour = parseTimeToHour(timeDraft.endTime);

    if (!timeDraft.date || startHour === null || endHour === null || endHour <= startHour) {
      return;
    }

    onUpdate(event.id, {
      date: timeDraft.date,
      startHour,
      durationHours: Number((endHour - startHour).toFixed(2)),
    });
    setEditMode('none');
  };

  const handleSavePlace = (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();

    const location = placeDraft.trim();
    if (!location) {
      return;
    }

    onUpdate(event.id, { location });
    setEditMode('none');
  };

  const handleRemove = () => {
    onRemove(event.id);
    setEditMode('none');
  };

  return (
    <section className={`v2-calendar-event-details ${className || ''}`.trim()}>
      <header>
        <div>
          <span className={`v2-calendar-event-details__category v2-calendar-event-details__category--${event.category}`}>
            {capitalize(event.category)}
          </span>
          <h2>{event.title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close event details">×</button>
      </header>
      <dl>
        {trip && (
          <div>
            <dt>Trip</dt>
            <dd className="v2-calendar-event-details__trip">
              <span className={`v2-calendar-page__legend-dot v2-calendar-page__legend-dot--${trip.color}`} />
              {trip.name}
            </dd>
          </div>
        )}
        <div>
          <dt>When</dt>
          <dd>{formatDayTitle(fromDateKey(event.date))}, {formatEventTime(event.startHour)} · {event.durationHours}h</dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>{event.location}</dd>
        </div>
        {event.cost && (
          <div>
            <dt>Cost</dt>
            <dd>{event.cost}</dd>
          </div>
        )}
        {event.notes && (
          <div>
            <dt>Notes</dt>
            <dd className="v2-calendar-event-details__notes">{event.notes}</dd>
          </div>
        )}
      </dl>

      {editMode === 'time' && (
        <form className="v2-calendar-event-details__edit-form" onSubmit={handleSaveTime}>
          <label>
            Date
            <input
              type="date"
              value={timeDraft.date}
              onChange={(changeEvent) => setTimeDraft((current) => ({
                ...current,
                date: changeEvent.target.value,
              }))}
              required
            />
          </label>
          <div className="v2-calendar-event-details__edit-row">
            <label>
              Start
              <input
                type="time"
                value={timeDraft.startTime}
                onChange={(changeEvent) => setTimeDraft((current) => ({
                  ...current,
                  startTime: changeEvent.target.value,
                }))}
                required
              />
            </label>
            <label>
              End
              <input
                type="time"
                value={timeDraft.endTime}
                onChange={(changeEvent) => setTimeDraft((current) => ({
                  ...current,
                  endTime: changeEvent.target.value,
                }))}
                required
              />
            </label>
          </div>
          <div className="v2-calendar-event-details__edit-actions">
            <button type="button" onClick={() => setEditMode('none')}>Cancel</button>
            <button type="submit">Save time</button>
          </div>
        </form>
      )}

      {editMode === 'place' && (
        <form className="v2-calendar-event-details__edit-form" onSubmit={handleSavePlace}>
          <label>
            Place
            <input
              type="text"
              value={placeDraft}
              onChange={(changeEvent) => setPlaceDraft(changeEvent.target.value)}
              placeholder="Location or meeting point"
              required
            />
          </label>
          <div className="v2-calendar-event-details__edit-actions">
            <button type="button" onClick={() => setEditMode('none')}>Cancel</button>
            <button type="submit">Save place</button>
          </div>
        </form>
      )}

      {editMode === 'none' && (
        <div className="v2-calendar-event-details__actions">
          <button type="button" onClick={() => setEditMode('time')}>Edit time</button>
          <button type="button" onClick={() => setEditMode('place')}>Edit place</button>
          <button type="button" className="v2-calendar-event-details__remove" onClick={handleRemove}>Remove</button>
        </div>
      )}
    </section>
  );
};

interface AddActivityModalProps {
  selectedDate: Date;
  draft: ActivityDraft;
  trips: CalendarTrip[];
  onDraftChange: (field: keyof ActivityDraft, value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
}

const AddActivityModal: React.FC<AddActivityModalProps> = ({
  selectedDate,
  draft,
  trips,
  onDraftChange,
  onSubmit,
  onCancel,
}) => (
  <div className="v2-calendar-activity-modal" role="dialog" aria-modal="true" aria-labelledby="calendar-activity-modal-title">
    <button type="button" className="v2-calendar-activity-modal__backdrop" aria-label="Close add activity form" onClick={onCancel} />
    <section className="v2-calendar-activity-modal__panel">
      <header>
        <div>
          <p>{formatDayTitle(selectedDate)}</p>
          <h2 id="calendar-activity-modal-title">Add activity</h2>
        </div>
        <button type="button" onClick={onCancel} aria-label="Close add activity form">×</button>
      </header>

      <form className="v2-calendar-activity-modal__form" onSubmit={onSubmit}>
        <label>
          Activity
          <input
            type="text"
            value={draft.title}
            onChange={(event) => onDraftChange('title', event.target.value)}
            placeholder="e.g. Sintra day trip"
            required
          />
        </label>

        <label>
          Trip <span>optional</span>
          <select
            value={draft.tripId || ''}
            onChange={(event) => onDraftChange('tripId', event.target.value)}
          >
            <option value="">No trip</option>
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>{trip.name}</option>
            ))}
          </select>
        </label>

        <div className="v2-calendar-activity-modal__row">
          <label>
            Start
            <input
              type="time"
              value={draft.startTime}
              onChange={(event) => onDraftChange('startTime', event.target.value)}
              required
            />
          </label>
          <label>
            End
            <input
              type="time"
              value={draft.endTime}
              onChange={(event) => onDraftChange('endTime', event.target.value)}
              required
            />
          </label>
        </div>

        <label>
          Place <span>optional</span>
          <input
            type="text"
            value={draft.location}
            onChange={(event) => onDraftChange('location', event.target.value)}
            placeholder="Location or meeting point"
          />
        </label>

        <div className="v2-calendar-activity-modal__row">
          <label>
            Cost <span>optional</span>
            <input
              type="text"
              value={draft.cost}
              onChange={(event) => onDraftChange('cost', event.target.value)}
              placeholder="$0"
            />
          </label>
          <label>
            Type
            <select
              value={draft.category}
              onChange={(event) => onDraftChange('category', event.target.value)}
            >
              <option value="activity">Activity</option>
              <option value="travel">Travel</option>
              <option value="food">Food & dining</option>
              <option value="lodging">Lodging</option>
            </select>
          </label>
        </div>

        <label>
          Notes <span>optional</span>
          <textarea
            value={draft.notes}
            onChange={(event) => onDraftChange('notes', event.target.value)}
            placeholder="Anything to remember for this activity"
          />
        </label>

        <div className="v2-calendar-activity-modal__actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button type="submit">Add activity</button>
        </div>
      </form>
    </section>
  </div>
);

function createEmptyActivityDraft(): ActivityDraft {
  return {
    title: '',
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    cost: '',
    notes: '',
    category: 'activity',
    tripId: '',
  };
}

interface MonthCell {
  date: Date;
  dateKey: string;
  inMonth: boolean;
}

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function startOfMonth(date: Date): Date {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
}

function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return startOfDay(next);
}

function addMonths(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + amount);
  return startOfDay(next);
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return startOfDay(new Date(year, month - 1, day));
}

function getWeekdayLabel(date: Date): string {
  const labels = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return labels[date.getDay()];
}

function getWeekDays(date: Date): CalendarDay[] {
  const weekStart = startOfWeek(date);
  return Array.from({ length: 7 }, (_, index) => {
    const dayDate = addDays(weekStart, index);
    return {
      label: getWeekdayLabel(dayDate),
      date: dayDate,
      dateKey: toDateKey(dayDate),
    };
  });
}

function startOfWeek(date: Date): Date {
  const normalized = startOfDay(date);
  const day = normalized.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(normalized, diff);
}

function buildMonthCells(monthDate: Date): MonthCell[] {
  const monthStart = startOfMonth(monthDate);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const cells: MonthCell[] = [];

  for (let index = 0; index < 42; index += 1) {
    const date = addDays(gridStart, index);
    cells.push({
      date,
      dateKey: toDateKey(date),
      inMonth: date.getMonth() === monthStart.getMonth(),
    });
  }

  return cells;
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatDayTitle(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWeekRange(start: Date, end: Date): string {
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
  }

  return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`;
}

function parseTimeToHour(time: string): number | null {
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours + minutes / 60;
}

function formatHour(hour: number): string {
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function formatEventTime(hour: number): string {
  const wholeHour = Math.floor(hour);
  const minutes = Math.round((hour - wholeHour) * 60);
  return `${String(wholeHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function mapTripToCalendarTrip(trip: Trip, index: number): CalendarTrip {
  return {
    id: String(trip.id),
    name: trip.title,
    color: TRIP_COLORS[index % TRIP_COLORS.length],
    startDate: trip.dates?.startDate,
    endDate: trip.dates?.endDate,
    destinations: trip.destinations,
  };
}

function mapActivityToCalendarEvent(activity: Activity, tripId: string): CalendarEvent | null {
  if (!activity.dateTime) {
    return null;
  }

  const start = new Date(activity.dateTime);
  if (Number.isNaN(start.getTime())) {
    return null;
  }

  const location = activity.location?.trim() || 'TBD';
  const startHour = start.getHours() + start.getMinutes() / 60;

  return {
    id: String(activity.id),
    title: activity.title,
    meta: location,
    date: toDateKey(start),
    startHour,
    durationHours: DEFAULT_ACTIVITY_DURATION_HOURS,
    category: inferActivityCategory(activity.title, location),
    tripId,
    location,
    cost: formatBudget(activity.budget) || undefined,
  };
}

function inferActivityCategory(title: string, location: string): CalendarEvent['category'] {
  const text = `${title} ${location}`.toLowerCase();

  if (/(flight|train|bus|drive|taxi|transfer|airport)/.test(text)) {
    return 'travel';
  }
  if (/(hotel|check-in|check in|lodging|hostel|airbnb|suite)/.test(text)) {
    return 'lodging';
  }
  if (/(dinner|lunch|breakfast|food|restaurant|cafe|coffee|market|wine)/.test(text)) {
    return 'food';
  }
  return 'activity';
}

function buildTripDateKeys(trips: CalendarTrip[]): Set<string> {
  const keys = new Set<string>();

  trips.forEach((trip) => {
    if (!trip.startDate || !trip.endDate) {
      return;
    }

    const start = fromDateKey(trip.startDate);
    const end = fromDateKey(trip.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    const cursor = startOfDay(start);
    while (cursor <= end) {
      keys.add(toDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return keys;
}

function getTripsInWeek(trips: CalendarTrip[], weekDays: CalendarDay[]): CalendarTrip[] {
  const weekDateKeys = new Set(weekDays.map((day) => day.dateKey));

  return trips.filter((trip) => {
    if (!trip.startDate || !trip.endDate) {
      return false;
    }

    const start = fromDateKey(trip.startDate);
    const end = fromDateKey(trip.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    const cursor = startOfDay(start);
    while (cursor <= end) {
      if (weekDateKeys.has(toDateKey(cursor))) {
        return true;
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return false;
  });
}

function formatTripAllDayLabel(trip: CalendarTrip): string {
  const destinations = trip.destinations?.filter(Boolean) || [];
  if (destinations.length === 0) {
    return trip.name;
  }
  return `${trip.name} · ${destinations.join(' → ')}`;
}

export default CalendarPage;
